import { fetchStudent, fetchStudentsByFamily } from './studentService';

/**
 * principalStudents — resolve "the students this signed-in person may read".
 *
 * WHY THIS EXISTS (P2b, 2026-08-18). Every customer-portal surface opened by
 * calling `fetchStudentsByFamily(userProfile.familyId)`. That is correct for a
 * guardian and DENIED for a student.
 *
 * The trap is that it does not look denied. `performClaimStudentAccount` writes
 * BOTH scope scalars onto a student's users doc — `familyId` *and* `studentId`
 * — so every `if (!familyId) return` guard on these pages passes for a student,
 * and the family-scoped LIST is then refused by the rules, which admit a student
 * only to a doc get of their own record (`firestore.rules:152-156`). The page
 * renders empty rather than erroring, so the surface looks built and broken
 * instead of unbuilt.
 *
 * RULES ARE NOT FILTERS, and this module is the client half of that discipline
 * for `students`: it does not trim a family list down to one child, it issues a
 * DIFFERENT QUERY per principal, because the wide query is refused whole rather
 * than trimmed. Everything downstream of it already mirrors correctly —
 * `fetchTimeSlotsForStudents` constrains `status == 'assigned'`
 * (`timeSlotService.js:52-67`) and `fetchDeliveredNotesForStudent` constrains
 * `status in ['focus','landed']`, which are exactly the predicates the student
 * rule branches carry. Only the entry point was wrong.
 *
 * H17 (customer-principal-model.md): "own record only" must be TESTED, not
 * assumed. The student branch keys on `studentId` and never on `familyId`, so a
 * sibling is unreachable even though the two share a family. The test that pins
 * that lives beside this file; deleting the distinction must fail it.
 */

/** A student principal reads exactly one record: their own. */
const STUDENT_ROLE = 'student';

/**
 * Resolve the readable student set for a principal.
 *
 * @param {object|null} userProfile - the users doc from AuthContext
 * @returns {Promise<Array>} students the principal may read; [] when unresolvable
 */
export async function fetchStudentsForPrincipal(userProfile) {
	if (!userProfile) return [];

	if (userProfile.role === STUDENT_ROLE) {
		// Doc get, never a list. A missing studentId is a malformed users doc
		// rather than an empty family: fail to an empty set, which renders the
		// page's own empty state, instead of falling back to the family list —
		// that fallback would be a read the rules refuse anyway, and wanting it
		// is how "own record only" quietly becomes "family record".
		if (!userProfile.studentId) return [];
		const student = await fetchStudent(userProfile.studentId);
		return student ? [student] : [];
	}

	if (!userProfile.familyId) return [];
	return fetchStudentsByFamily(userProfile.familyId);
}

/**
 * True only when we POSITIVELY know this principal is a student.
 *
 * Deliberately phrased in the positive, and deliberately fail-open: an
 * unresolved profile is not a student, so a guardian mid-load keeps every
 * affordance. Callers use it for COPY and AFFORDANCES, never for access —
 * hiding a link is a courtesy, the route guard is the enforcement, and neither
 * substitutes for the other. Written the other way round ("isFamilyPrincipal",
 * false for a null profile) it silently stripped a loading guardian's own menu.
 */
export function isStudentPrincipal(userProfile) {
	return userProfile?.role === STUDENT_ROLE;
}

export default fetchStudentsForPrincipal;
